import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Target, Clock, MessageSquare, Megaphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Progress, Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const PetitionDetail = () => {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [data, setData] = useState<any>(null);
  const [isPublic, setIsPublic] = useState(false);
  const [signComment, setSignComment] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [upd, setUpd] = useState({ title: "", body: "" });

  const load = () => api.get(`/petitions/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, [id, user]);

  const sign = async () => {
    if (!user) return login();
    setBusy(true);
    try {
      await api.post(`/petitions/${id}/sign`, { public: isPublic, comment: signComment || undefined });
      toast.success("Signature recorded. Thank you.");
      setSignComment("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const postComment = async () => {
    if (!user) return login();
    if (!comment.trim()) return;
    try {
      await api.post(`/petitions/${id}/comments`, { body: comment });
      setComment("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const postUpdate = async () => {
    if (!upd.title.trim() || !upd.body.trim()) return toast.error("Add a title and body");
    try {
      await api.post(`/petitions/${id}/updates`, upd);
      setUpd({ title: "", body: "" });
      toast.success("Update posted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (data === null) return <Loader />;
  if (data === false) return <div className="mx-auto max-w-[1400px] px-5 py-32 text-white/50" data-testid="petition-not-found">Petition not found.</div>;

  const p = data.petition;
  const isOwner = user && (user.user_id === p.creator_id || user.role === "admin");

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid gap-12 lg:grid-cols-12" data-testid="petition-detail-page">
      <div className="lg:col-span-7">
        <div className="flex gap-2">
          <Pill tone="blue">{p.category}</Pill>
          {p.status !== "approved" && <Pill tone="yellow">{p.status}</Pill>}
        </div>
        <h1 className="mt-5 font-bebas text-5xl sm:text-6xl tracking-wide uppercase leading-[0.92]" data-testid="petition-title">{p.title}</h1>
        <div className="mt-6 flex flex-wrap gap-6 text-[12px] uppercase tracking-[0.18em] text-white/50">
          <span className="flex items-center gap-2"><Target size={14} className="text-neon" /> {p.target_authority}</span>
          {p.deadline && <span className="flex items-center gap-2"><Clock size={14} className="text-neon" /> {new Date(p.deadline).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>}
          <span>By {p.creator_name}</span>
        </div>
        <p className="mt-8 text-lg text-white/70 leading-relaxed">{p.summary}</p>
        <div className="mt-6 whitespace-pre-line text-sm text-white/55 leading-relaxed" data-testid="petition-body">{p.body}</div>

        {p.updates?.length > 0 && (
          <div className="mt-14" data-testid="petition-updates">
            <h3 className="font-bebas text-3xl tracking-wide uppercase">Updates</h3>
            <div className="mt-6 grid gap-4">
              {p.updates.map((u: any, i: number) => (
                <div key={i} className="border-l-2 border-neon pl-5 py-1">
                  <div className="font-outfit font-semibold">{u.title}</div>
                  <div className="mt-1 text-sm text-white/55">{u.body}</div>
                  <div className="mt-2 text-[11px] uppercase tracking-widest text-white/30">{new Date(u.created_at).toLocaleDateString("en-IN")}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isOwner && (
          <div className="mt-14 border border-white/10 bg-ink-surface p-6" data-testid="post-update-block">
            <h4 className="flex items-center gap-2 font-outfit font-semibold"><Megaphone size={16} className="text-neon" /> Post an update</h4>
            <input value={upd.title} onChange={(e) => setUpd({ ...upd, title: e.target.value })} placeholder="Update title" data-testid="update-title-input" className="mt-4 w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors" />
            <textarea value={upd.body} onChange={(e) => setUpd({ ...upd, body: e.target.value })} placeholder="What changed?" rows={3} data-testid="update-body-input" className="mt-3 w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
            <button onClick={postUpdate} data-testid="post-update-btn" className="mt-3 bg-neon px-6 h-10 text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow">Publish update</button>
          </div>
        )}

        <div className="mt-14" data-testid="petition-comments">
          <h3 className="flex items-center gap-2 font-bebas text-3xl tracking-wide uppercase"><MessageSquare size={18} className="text-neon" /> Comments ({data.comments.length})</h3>
          <div className="mt-6 flex gap-3">
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder={user ? "Add your voice" : "Sign in to comment"} data-testid="comment-input" className="flex-1 h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors" />
            <button onClick={postComment} data-testid="comment-submit" className="border border-white/20 px-5 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">Post</button>
          </div>
          <div className="mt-8 grid gap-5">
            {data.comments.map((c: any) => (
              <div key={c.comment_id} className="border-b border-white/8 pb-5">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-semibold">{c.user_name}</span>
                  <span className="text-[11px] uppercase tracking-widest text-white/30">{new Date(c.created_at).toLocaleDateString("en-IN")}</span>
                </div>
                <p className="mt-2 text-sm text-white/60">{c.body}</p>
              </div>
            ))}
            {data.comments.length === 0 && <p className="text-sm text-white/35">No comments yet.</p>}
          </div>
        </div>
      </div>

      <aside className="lg:col-span-5">
        <div className="border border-white/10 bg-ink-surface p-7 sticky top-24">
          <div className="flex items-end justify-between">
            <span className="font-bebas text-6xl tracking-wide" data-testid="signature-count">{p.signature_count}</span>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">of {p.goal} goal</span>
          </div>
          <div className="mt-4"><Progress value={p.signature_count} goal={p.goal} /></div>

          {data.has_signed ? (
            <div className="mt-7 border border-neon/40 bg-neon/10 p-4 text-sm text-white/80" data-testid="already-signed">
              You've signed this petition. Share it to build pressure.
            </div>
          ) : (
            <div className="mt-7">
              <textarea
                value={signComment}
                onChange={(e) => setSignComment(e.target.value)}
                rows={3}
                placeholder="Why does this matter to you? (optional)"
                data-testid="sign-comment-input"
                className="w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors"
              />
              <label className="mt-4 flex items-start gap-3 text-sm text-white/60 cursor-pointer">
                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="public-signer-toggle" className="mt-1 accent-[#FF003C]" />
                Show my name publicly as a signer (default is private)
              </label>
              <button
                onClick={sign}
                disabled={busy}
                data-testid="sign-petition-btn"
                className="mt-5 w-full h-12 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] disabled:opacity-40 hover:pc-glow transition-shadow duration-200"
              >
                {user ? "Sign this petition" : "Sign in to sign"}
              </button>
            </div>
          )}

          <div className="mt-8 border-t border-white/10 pt-6">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Public signers</h4>
            <div className="mt-4 grid gap-2 text-sm text-white/60" data-testid="public-signers">
              {data.public_signers.map((s: any, i: number) => (
                <div key={i} className="flex justify-between">
                  <span>{s.name}</span>
                  <span className="text-white/30 text-xs">{s.city || "—"}</span>
                </div>
              ))}
              {data.public_signers.length === 0 && <p className="text-white/30 text-sm">All signers are private so far.</p>}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default PetitionDetail;
